import React, { useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Divider,
  Card,
  CardContent,
  Chip,
  AppBar,
  Toolbar,
  useTheme,
  useMediaQuery,
  CssBaseline,
  ThemeProvider,
  createTheme,
  alpha,
  IconButton,
} from '@mui/material';
import {
  School,
  Gavel,
  Security,
  PrivacyTip,
  Payment,
  DataUsage,
  Update,
  ContactSupport,
  CheckCircle,
  WarningAmber,
  ArrowBack,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Custom theme with JMK branding
const theme = createTheme({
  palette: {
    primary: {
      main: '#1a237e',
      light: '#3949ab',
      dark: '#0d1445',
    },
    secondary: {
      main: '#ff6f00',
      light: '#ffa040',
      dark: '#c43e00',
    },
    background: {
      default: '#f5f7fa',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h6: {
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
  },
});

// Section data with icons
const sections = [
  { id: 1, title: 'About the Platform', icon: React.createElement(School) },
  { id: 2, title: 'Acceptance of Terms', icon: React.createElement(Gavel) },
  { id: 3, title: 'Eligibility', icon: React.createElement(School) },
  { id: 4, title: 'School Responsibilities', icon: React.createElement(School) },
  { id: 5, title: 'User Responsibilities', icon: React.createElement(School) },
  { id: 6, title: 'Subscription and Payments', icon: React.createElement(Payment) },
  { id: 7, title: 'Data Ownership', icon: React.createElement(DataUsage) },
  { id: 8, title: 'Data Security', icon: React.createElement(Security) },
  { id: 9, title: 'Privacy', icon: React.createElement(PrivacyTip) },
  { id: 10, title: 'Platform Availability', icon: React.createElement(DataUsage) },
  { id: 11, title: 'Intellectual Property', icon: React.createElement(Gavel) },
  { id: 12, title: 'Prohibited Activities', icon: React.createElement(WarningAmber) },
  { id: 13, title: 'Third-Party Services', icon: React.createElement(DataUsage) },
  { id: 14, title: 'Suspension and Termination', icon: React.createElement(WarningAmber) },
  { id: 15, title: 'Limitation of Liability', icon: React.createElement(WarningAmber) },
  { id: 16, title: 'Backup and Data Retention', icon: React.createElement(DataUsage) },
  { id: 17, title: 'Updates and New Features', icon: React.createElement(Update) },
  { id: 18, title: 'Confidentiality', icon: React.createElement(PrivacyTip) },
  { id: 19, title: 'Governing Law', icon: React.createElement(Gavel) },
  { id: 20, title: 'Amendments', icon: React.createElement(Update) },
  { id: 21, title: 'Contact Information', icon: React.createElement(ContactSupport) },
];

// Section content
const sectionContent = {
  1: 'The JMK School Management System is a Software-as-a-Service (SaaS) platform designed to help educational institutions manage admissions, students, staff, attendance, examinations, fees, transport, library, communication, reports, and other administrative activities.',
  2: 'By registering for, subscribing to, or using the Platform, the School and all authorized users acknowledge that they have read, understood, and agreed to these Terms and any applicable policies.',
  3: 'The Platform may be used only by: Registered Schools, School Administrators, Teachers, Students, Parents/Guardians, School Staff, Other users authorized by the School. The School is responsible for managing user accounts and permissions.',
  4: 'The School agrees to: Provide accurate and complete information. Maintain the confidentiality of login credentials. Ensure only authorized users access the Platform. Comply with all applicable laws and educational regulations. Maintain accurate student, staff, and financial records. Obtain any necessary parental or guardian consent required by law.',
  5: 'Each user agrees to: Use the Platform only for lawful educational and administrative purposes. Keep passwords confidential. Not share accounts with unauthorized persons. Not attempt to gain unauthorized access to any part of the Platform. Not upload malware, viruses, or harmful content. Not interfere with the security or operation of the Platform.',
  6: 'Access to the Platform may require a paid subscription. Subscription fees must be paid according to the selected plan. Subscription fees are non-refundable unless otherwise agreed in writing. Failure to make timely payments may result in suspension or termination of services. Taxes, if applicable, are the responsibility of the subscribing School.',
  7: 'All student, staff, academic, attendance, examination, fee, and institutional data entered into the Platform remains the property of the respective School. JMK does not claim ownership of the School\'s data.',
  8: 'JMK implements commercially reasonable administrative, technical, and organizational measures to protect customer data. While every effort is made to safeguard information, no internet-based system can guarantee absolute security. Schools are responsible for maintaining secure passwords and preventing unauthorized access to their accounts.',
  9: 'JMK processes information only to provide, maintain, secure, and improve the Platform. Personal information is handled in accordance with applicable privacy and data protection laws.',
  10: 'JMK aims to provide reliable service but does not guarantee uninterrupted or error-free operation. Service interruptions may occur due to: Scheduled maintenance, Software updates, Network failures, Third-party service outages, Force majeure events.',
  11: 'The Platform, including its software, source code, design, user interface, reports, documentation, trademarks, logos, and other intellectual property, is owned exclusively by JMK. Schools receive only a limited, non-transferable, non-exclusive license to use the Platform during the active subscription period.',
  12: 'Users shall not: Reverse engineer or copy the software. Resell, sublicense, or distribute the Platform. Attempt to bypass security features. Use automated tools to extract data without authorization. Upload unlawful, offensive, or copyrighted content without permission. Use the Platform for any illegal activity.',
  13: 'The Platform may integrate with third-party providers, including payment gateways, SMS services, email providers, cloud storage, mapping services, or video conferencing platforms. JMK is not responsible for the availability, policies, or performance of third-party services.',
  14: 'JMK reserves the right to suspend or terminate access if: Subscription payments remain overdue. These Terms are violated. Fraudulent or illegal activities are detected. Unauthorized access attempts are made. The Platform is misused.',
  15: 'To the maximum extent permitted by law, JMK shall not be liable for: Loss of data resulting from user actions. Internet or network failures. Third-party service interruptions. Hardware failures. Unauthorized access due to compromised credentials. Loss of profits, business interruption, or indirect, incidental, special, or consequential damages. JMK\'s total liability, if any, shall not exceed the subscription fees paid by the School during the preceding twelve (12) months.',
  16: 'JMK may maintain periodic backups for operational purposes. Schools are encouraged to retain copies of critical records. Upon termination of the subscription, data may be retained for a limited period as specified by JMK\'s data retention policy before secure deletion.',
  17: 'JMK may add, modify, improve, or discontinue features, modules, or services without prior notice when necessary to improve the Platform or maintain security.',
  18: 'Both JMK and the School agree to maintain the confidentiality of proprietary and confidential information obtained during the course of using the Platform, except where disclosure is required by law.',
  19: 'These Terms shall be governed by and interpreted in accordance with the laws of India. Any disputes arising out of or relating to these Terms or the use of the Platform shall be subject to the exclusive jurisdiction of the courts located in Nagpur, Maharashtra, India.',
  20: 'JMK reserves the right to update or modify these Terms at any time. Continued use of the Platform after such changes constitutes acceptance of the revised Terms.',
  21: 'For questions regarding these Terms and Conditions, please contact: JMK – School Management System Email: jmk.infotech2021@gmail.com Address: Nagpur, Maharashtra, India.',
};

// Disable right-click globally
const disableRightClick = (e) => {
  e.preventDefault();
  return false;
};

// Disable keyboard shortcuts
const disableKeyboardShortcuts = (e) => {
  // Ctrl+C, Ctrl+A, Ctrl+U, F12, Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+S
  if (
    (e.ctrlKey && ['c', 'C', 'a', 'A', 'u', 'U', 's', 'S'].includes(e.key)) ||
    e.key === 'F12' ||
    (e.ctrlKey && e.shiftKey && ['i', 'I', 'j', 'J'].includes(e.key)) ||
    (e.metaKey && ['c', 'C', 'a', 'A', 'u', 'U', 's', 'S'].includes(e.key))
  ) {
    e.preventDefault();
    return false;
  }
};

// Disable text selection via CSS and JS
const disableTextSelection = (e) => {
  // Additional protection - prevent selection via mouse events
  const target = e.target;
  if (target && target.closest && target.closest('.no-select')) {
    e.preventDefault();
    return false;
  }
};

const TermsAndConditions = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  useEffect(() => {
    // Add event listeners for protection
    document.addEventListener('contextmenu', disableRightClick);
    document.addEventListener('keydown', disableKeyboardShortcuts);
    document.addEventListener('selectstart', disableTextSelection);
    document.addEventListener('dragstart', disableTextSelection);

    // Prevent copy/paste
    const preventCopy = (e) => e.preventDefault();
    document.addEventListener('copy', preventCopy);
    document.addEventListener('cut', preventCopy);

    return () => {
      document.removeEventListener('contextmenu', disableRightClick);
      document.removeEventListener('keydown', disableKeyboardShortcuts);
      document.removeEventListener('selectstart', disableTextSelection);
      document.removeEventListener('dragstart', disableTextSelection);
      document.removeEventListener('copy', preventCopy);
      document.removeEventListener('cut', preventCopy);
    };
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        className="no-select"
        sx={{
          userSelect: 'none',
          WebkitUserSelect: 'none',
          MozUserSelect: 'none',
          msUserSelect: 'none',
          minHeight: '100vh',
          backgroundColor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Sticky Header */}
        {/* <AppBar
          position="sticky"
          elevation={0}
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.primary.light} 100%)`,
            borderBottom: `3px solid ${theme.palette.secondary.main}`,
            zIndex: 1100,
          }}
        >
          <Toolbar sx={{ py: isMobile ? 1 : 1.5, px: isMobile ? 2 : 4 }}> */}
        <Box sx={{ display: 'flex', alignItems: 'center', width: '90%' }}>
          <School
            sx={{
              mr: 2,
              fontSize: isMobile ? 28 : 36,
              color: alpha('#fff', 0.9),
            }}
          />
          <Box sx={{ flexGrow: 1 }}>
            {/* <Typography
                  variant={isMobile ? 'subtitle1' : 'h5'}
                  sx={{
                    fontWeight: 700,
                    color: '#fff',
                    letterSpacing: '-0.01em',
                    lineHeight: 1.2,
                  }}
                >
                  JMK School Management System
                </Typography> */}
            <IconButton onClick={() => navigate(-1)} color="primary">
              <ArrowBack />
              <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main, mb: 1 }}>
                Terms & Conditions
              </Typography>
            </IconButton>
          </Box>
          <Chip
            label="Effective Date: 2026"
            size="small"
            sx={{
              backgroundColor: alpha(theme.palette.secondary.main, 0.25),
              color: '#fff',
              fontWeight: 500,
              border: `1px solid ${alpha('#fff', 0.15)}`,
              '& .MuiChip-label': {
                px: 1.5,
              },
            }}
          />
        </Box>
        {/* </Toolbar>
        </AppBar> */}

        {/* Scrollable Content */}
        <Container
          maxWidth="lg"
          sx={{
            flex: 1,
            py: { xs: 3, md: 5 },
            px: { xs: 2, sm: 3, md: 4 },
          }}
        >
          {/* Acceptance Banner */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.5, sm: 3.5 },
              mb: 4,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, ${alpha(theme.palette.secondary.main, 0.04)} 100%)`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <CheckCircle sx={{ color: theme.palette.success.main, fontSize: 32 }} />
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                By creating an account, subscribing to, or using the JMK School Management System,
                the School and its authorized users acknowledge that they have read, understood, and agreed to these Terms and Conditions.
              </Typography>
            </Box>
          </Paper>

          {/* Sections Grid */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: '1fr 1fr',
              },
              gap: 3,
            }}
          >
            {sections.map((section) => (
              <Card
                key={section.id}
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.06)}`,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
                    borderColor: alpha(theme.palette.primary.main, 0.12),
                    transform: 'translateY(-2px)',
                  },
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <CardContent sx={{ p: 3, flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                        color: theme.palette.primary.main,
                        mr: 1.5,
                        flexShrink: 0,
                      }}
                    >
                      {section.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: { xs: '1rem', sm: '1.05rem' },
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                      }}
                    >
                      {section.id}. {section.title}
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 1.5, borderColor: alpha(theme.palette.primary.main, 0.06) }} />
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      lineHeight: 1.7,
                      fontSize: '0.95rem',
                    }}
                  >
                    {sectionContent[section.id]}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>

          {/* Footer Section */}
          <Box
            component="footer"
            sx={{
              mt: 5,
              pt: 4,
              pb: 2,
              textAlign: 'center',
              borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontWeight: 400,
                letterSpacing: '0.3px',
              }}
            >
              © {new Date().getFullYear()} JMK School Management System. All rights reserved.
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: alpha(theme.palette.text.secondary, 0.6),
                display: 'block',
                mt: 0.5,
                fontStyle: 'italic',
              }}
            >
              Developed with ❤️ in Nagpur, Maharashtra, India
            </Typography>
          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default TermsAndConditions;